import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

/**
 * Decides whether the grid toolbar's filter bar should be ordered after the pagination.
 *
 * The bar belongs beside the search field while its chips fit on the toolbar row, and below the
 * pagination once they do not — and CSS cannot express that on its own. `flex-basis: max-content`
 * makes the bar WRAP at the right moment, but flexbox lays wrapped items out in order, so a bar
 * ordered next to the search field takes the actions and the pagination down with it. Reordering
 * is the only way to leave them behind, and `order` cannot react to whether a wrap happened.
 *
 * So the wrap is measured instead: if the bar sits on a lower row than the search field, it wrapped.
 * The caller turns that into a class, and the class is what carries `order`.
 *
 * This converges rather than oscillating. Whether a set of flex items fits on one line depends on
 * the sum of their base sizes, not on their order — so a bar that fits when ordered last also fits
 * when ordered second, and one that does not fit, does not fit either way. The measurement
 * therefore reads the same before and after the class is applied.
 */
export function useFilterBarWrap(
    filterBar: Ref<HTMLElement | { $el?: HTMLElement } | null>,
    anchor: Ref<HTMLElement | null>,
) {
    const wrapped = ref(false)
    let observer: ResizeObserver | null = null

    const elementOf = (value: unknown): HTMLElement | null => {
        if (!value) return null
        if (value instanceof HTMLElement) return value
        const el = (value as { $el?: unknown }).$el
        return el instanceof HTMLElement ? el : null
    }

    const measure = (): void => {
        const bar = elementOf(filterBar.value)
        const row = elementOf(anchor.value)
        if (!bar || !row) {
            wrapped.value = false
            return
        }

        // Overlap, not `bar.offsetTop > row.offsetTop`. The toolbar centres its items, so two
        // controls of different heights on the SAME row have different tops — a 30px filter bar
        // beside a 38px search field starts 4px lower and a top comparison calls that a wrap.
        // Two boxes share a row when their vertical extents overlap at all.
        //
        // offsetTop, not getBoundingClientRect: both share an offset parent, and offsetTop is
        // unaffected by the sticky toolbar's scroll position the way viewport coordinates are.
        const barTop = bar.offsetTop
        const barBottom = barTop + bar.offsetHeight
        const rowTop = row.offsetTop
        const rowBottom = rowTop + row.offsetHeight

        wrapped.value = barTop >= rowBottom || rowTop >= barBottom
    }

    onMounted(() => {
        measure()
        if (typeof ResizeObserver === 'undefined') return

        // Watch both: the toolbar resizing changes how much room is left, and the bar's own chips
        // changing changes how much room it needs. Either can flip the answer.
        observer = new ResizeObserver(() => { measure() })
        const bar = elementOf(filterBar.value)
        const row = elementOf(anchor.value)
        if (bar) observer.observe(bar)
        if (row?.parentElement) observer.observe(row.parentElement)
    })

    onBeforeUnmount(() => {
        observer?.disconnect()
        observer = null
    })

    return { wrapped, measure }
}
